import type {
  AcknowledgePublicationInput,
  ApplyRemotePublicationInput,
  BeginPublicationAttemptInput,
  DocPublicationStorage,
  LocalPublication,
} from '@streamlain/doc-publication-contract';
import type {
  RefusedPublicationResolution,
  RefusedPublicationStorage,
} from '@streamlain/doc-publication-contract';
import { revivePublicationError } from '@streamlain/doc-publication-contract';
import type { DatabaseImportPort } from '@streamlain/doc-publication-contract/database-writer';
import type {
  DatabaseRowGroupTransaction,
  DatabaseRowGroupTransactionResult,
  DatabaseRowTransaction,
  DatabaseRowTransactionResult,
} from '@streamlain/doc-publication-contract/database-writer';
import type { WorkspaceSearchAdvancedPayload } from '@streamlain/permission-snapshot-contract';
import {
  type CancelablePromise,
  OpClient,
  transfer,
} from '@toeverything/infra/op';
import {
  from,
  lastValueFrom,
  type Observable,
  ReplaySubject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { v4 as uuid } from 'uuid';

import { DummyConnection } from '../connection';
import type {
  DatabaseWriterMutation,
  DatabaseWriterMutationResult,
} from '../crdt/database-writer-contract';
import {
  AwarenessFrontend,
  BlobFrontend,
  DocFrontend,
  IndexerFrontend,
} from '../frontend';
import {
  type AggregateOptions,
  type AggregateResult,
  type AwarenessRecord,
  type BlobRecord,
  type BlobStorage,
  type DemoContentRemovedEvent,
  type DocPersistedEvent,
  type DocRecord,
  type DocStorage,
  type DocUpdate,
  type IndexerSchema,
  type ListedBlobRecord,
  type Query,
  type SearchOptions,
  type SearchResult,
  type SyncProtocolUnsupportedEvent,
  type SystemDocAccessReadyEvent,
} from '../storage';
import type { AwarenessSync } from '../sync/awareness';
import type { BlobSync } from '../sync/blob';
import type { DocSync } from '../sync/doc';
import type {
  DatabaseSearchCoverage,
  DatabaseSearchGap,
  IndexerPreferOptions,
  IndexerSync,
  LocalSearchStats,
  SearchSnapshotFileNames,
  SearchSnapshotMails,
  SearchSnapshotPage,
} from '../sync/indexer';
import { traceImportAwait } from '../utils/import-seam-diagnostics';
import { takeUntilAbort } from '../utils/take-until-abort';
import { throwIfAborted } from '../utils/throw-if-aborted';
import type {
  DatabaseOptionNamesWorkerRequest,
  DatabaseOptionNamesWorkerResult,
  DatabaseProjectionWorkerRequest,
  DatabaseProjectionWorkerResult,
  StoreInitOptions,
  WorkerManagerOps,
  WorkerOps,
} from './ops';
export type {
  DatabaseOptionNamesWorkerRequest,
  DatabaseOptionNamesWorkerResult,
  DatabaseProjectionWorkerRequest,
  DatabaseProjectionWorkerResult,
} from './ops';
export type {
  UnboundStoreInitOptions as UnboundWorkerInitOptions,
  StoreInitOptions as WorkerInitOptions,
} from './ops';

/** Gate the existing transport API, including subscriptions, on provider readiness. */
function readyWorkerClient(
  client: OpClient<WorkerOps>,
  ready: Promise<unknown>,
  closedError: () => Error | undefined,
  disposed$: Observable<void>
): OpClient<WorkerOps> {
  let fulfilled = false;
  void ready.then(
    () => {
      fulfilled = true;
    },
    () => {}
  );
  return new Proxy(client, {
    get(target, property) {
      if (fulfilled && !closedError())
        return Reflect.get(target, property, target);
      if (property === 'call') {
        return (...args: unknown[]) => {
          const completion = Promise.withResolvers<unknown>();
          let dispatched: CancelablePromise<unknown> | undefined;
          let cancelled = false;
          const cancel = (reason: unknown) => {
            cancelled = true;
            dispatched?.cancel();
            completion.reject(reason);
          };
          const signal = args.at(-1);
          const onAbort = () => {
            if (signal instanceof AbortSignal) cancel(signal.reason);
          };
          if (signal instanceof AbortSignal) {
            if (signal.aborted) onAbort();
            else signal.addEventListener('abort', onAbort, { once: true });
          }
          const cleanup = () => {
            if (signal instanceof AbortSignal)
              signal.removeEventListener('abort', onAbort);
          };
          void completion.promise.then(cleanup, cleanup);
          void ready
            .then(() => {
              if (cancelled) return;
              const error = closedError();
              if (error) {
                completion.reject(error);
                return;
              }
              dispatched = Reflect.apply(target.call, target, args);
              dispatched!.then(completion.resolve, completion.reject);
            }, completion.reject)
            .catch(completion.reject);
          return Object.assign(completion.promise, {
            cancel: () => cancel('canceled'),
          });
        };
      }
      if (property === 'ob$') {
        return (...args: unknown[]) =>
          from(ready).pipe(
            switchMap(() => {
              const error = closedError();
              if (error) throw error;
              const stream$: Observable<unknown> = Reflect.apply(
                target.ob$,
                target,
                args
              );
              return stream$;
            }),
            // Owner disposal ends a pending observation before a late open can
            // reject it. Provider failures while still owned remain observable.
            takeUntil(disposed$)
          );
      }
      return Reflect.get(target, property, target);
    },
  });
}

function storeOpenFailureCode(error: unknown): string {
  if (error && typeof error === 'object') {
    const { code, name } = error as { code?: unknown; name?: unknown };
    if (typeof code === 'string') return code;
    if (typeof name === 'string') return name;
  }
  return 'UNKNOWN';
}

export class StoreManagerClient {
  private readonly connections = new Map<
    string,
    {
      store: StoreClient;
      dispose: () => void;
    }
  >();

  constructor(private readonly client: OpClient<WorkerManagerOps>) {}

  open(key: string, options: StoreInitOptions | PromiseLike<StoreInitOptions>) {
    const { port1, port2 } = new MessageChannel();
    const transport = new OpClient<WorkerOps>(port1, {
      reviveError: revivePublicationError,
    });
    const closeKey = uuid();
    const disposedError = new Error('Store disposed');
    const disposed$ = new ReplaySubject<void>(1);
    let disposed = false;
    let opened = false;
    let closing: Promise<void> | undefined;
    const cleanup = () => {
      transport.destroy();
      port1.close();
      port2.close();
    };
    const close = () => {
      closing ??= this.client
        .call('close', closeKey)
        .then(() => {})
        .finally(cleanup);
      return closing;
    };
    const ready = Promise.resolve(options)
      .then(options =>
        this.client.call(
          'open',
          transfer({ key, closeKey, options, port: port2 }, [port2])
        )
      )
      .then(async result => {
        opened = true;
        if (disposed) {
          await close();
          throw disposedError;
        }
        return result;
      });
    void ready.catch((error: unknown) => {
      cleanup();
      // A store that never opens leaves the page without sync and without any
      // other trace; the rejection handed to callers stays unchanged.
      if (error !== disposedError)
        console.error('store open failed', storeOpenFailureCode(error));
    });
    const client = readyWorkerClient(
      transport,
      ready,
      () => (disposed ? disposedError : undefined),
      disposed$
    );
    const connection = {
      store: new StoreClient(client, ready),
      ready,
      dispose: () => {
        if (disposed) return;
        disposed = true;
        disposed$.next();
        disposed$.complete();
        this.connections.delete(closeKey);
        if (opened) close().catch(err => console.error('error closing', err));
      },
    };
    this.connections.set(closeKey, connection);
    return connection;
  }

  dispose() {
    this.connections.forEach(connection => {
      connection.dispose();
    });
  }

  /**
   * Clears the local copy of a workspace for every tab; with a recovery
   * version only a copy older than it. Rejects with
   * LocalWorkspaceDataBusyError while another tab or window holds the data.
   */
  eraseSpace(workspaceId: string, recoveryVersion?: number): Promise<boolean> {
    return this.client.call('eraseSpace', { workspaceId, recoveryVersion });
  }

  /**
   * Ties the stores to the page: sync pauses on blur and resumes on focus or
   * click; a page shown again from the back/forward cache or made visible
   * checks the account lock, since a frozen page misses lock signals.
   */
  watchPage(page: Window) {
    page.addEventListener('beforeunload', () => this.dispose());
    page.addEventListener('focus', () => this.resume());
    page.addEventListener('click', () => this.resume());
    page.addEventListener('blur', () => this.pause());
    page.addEventListener('pageshow', event => {
      if (event.persisted) this.recheckAuthority();
    });
    page.document.addEventListener('visibilitychange', () => {
      if (page.document.visibilityState === 'visible') this.recheckAuthority();
    });
  }

  private recheckAuthority() {
    this.client.call('recheckAuthority').catch(err => {
      console.error('error checking the account lock', err);
    });
  }

  pause() {
    this.connections.forEach(connection => {
      connection.store.pauseSync().catch(err => {
        console.error('error pausing', err);
      });
    });
  }

  resume() {
    this.connections.forEach(connection => {
      connection.store.resumeSync().catch(err => {
        console.error('error resuming', err);
      });
    });
  }
}

export class StoreClient
  implements DocPublicationStorage, RefusedPublicationStorage
{
  constructor(
    private readonly client: OpClient<WorkerOps>,
    ready: Promise<unknown> = Promise.resolve()
  ) {
    this.docStorage = new WorkerDocStorage(this.client);
    this.blobStorage = new WorkerBlobStorage(this.client);
    this.docSync = new WorkerDocSync(this.client, ready);
    this.blobSync = new WorkerBlobSync(this.client);
    this.awarenessSync = new WorkerAwarenessSync(this.client);
    this.docFrontend = new DocFrontend(this.docStorage, this.docSync);
    this.blobFrontend = new BlobFrontend(this.blobStorage, this.blobSync);
    this.awarenessFrontend = new AwarenessFrontend(this.awarenessSync);
    this.indexerSync = new WorkerIndexerSync(this.client);
    this.indexerFrontend = new IndexerFrontend(this.indexerSync);
  }

  exportPortable(exportId: string, signal?: AbortSignal): Promise<void> {
    return this.client.call('docStorage.exportPortable', exportId, signal);
  }

  readPublicationBase(docIds: readonly string[], signal?: AbortSignal) {
    return this.docStorage.readPublicationBase(docIds, signal);
  }

  commitPublication(input: LocalPublication, signal?: AbortSignal) {
    return this.docStorage.commitPublication(input, signal);
  }

  listRefusedPublications(docIds: readonly string[]) {
    return this.docStorage.listRefusedPublications(docIds);
  }

  resolveRefusedPublications(
    docIds: readonly string[],
    resolution: RefusedPublicationResolution
  ) {
    return this.docStorage.resolveRefusedPublications(docIds, resolution);
  }

  listPendingPublications(after: number, limit: number) {
    return this.docStorage.listPendingPublications(after, limit);
  }

  beginPublicationAttempt(input: BeginPublicationAttemptInput) {
    return this.docStorage.beginPublicationAttempt(input);
  }

  acknowledgePublication(input: AcknowledgePublicationInput) {
    return this.docStorage.acknowledgePublication(input);
  }

  applyRemotePublication(
    input: ApplyRemotePublicationInput,
    signal?: AbortSignal
  ) {
    return this.docStorage.applyRemotePublication(input, signal);
  }

  private readonly docStorage: WorkerDocStorage;
  private readonly blobStorage: WorkerBlobStorage;
  private readonly blobSync: WorkerBlobSync;
  private readonly awarenessSync: WorkerAwarenessSync;
  private readonly indexerSync: WorkerIndexerSync;

  /**
   * Direct access to doc sync for corruption$ events.
   */
  readonly docSync: WorkerDocSync;
  readonly docFrontend: DocFrontend;
  readonly blobFrontend: BlobFrontend;
  readonly awarenessFrontend: AwarenessFrontend;
  readonly indexerFrontend: IndexerFrontend;

  subscribeDocUpdates(callback: (docId: string) => void): () => void {
    return this.docStorage.subscribeDocUpdate(update => callback(update.docId));
  }

  async projectDatabaseRows(
    input: DatabaseProjectionWorkerRequest,
    abort?: AbortSignal
  ): Promise<DatabaseProjectionWorkerResult> {
    abort?.throwIfAborted();
    return this.client.call('databaseQuery.project', input, abort);
  }

  /** Resolves once the listed documents are reconciled with every server. */
  async waitForDatabaseDocsReconciled(
    docIds: readonly string[],
    abort?: AbortSignal
  ): Promise<void> {
    abort?.throwIfAborted();
    await this.client.call('databaseQuery.reconciled', [...docIds], abort);
  }

  async readDatabaseOptionNames(
    input: DatabaseOptionNamesWorkerRequest,
    abort?: AbortSignal
  ): Promise<DatabaseOptionNamesWorkerResult> {
    abort?.throwIfAborted();
    return this.client.call('databaseQuery.optionNames', input, abort);
  }

  readonly databaseImport: DatabaseImportPort = {
    projection: input => this.client.call('databaseImport.projection', input),
    updates: input => this.client.call('databaseImport.updates', input),
    history: input => this.client.call('databaseImport.history', input),
    retainHistory: id => this.client.call('databaseImport.retainHistory', id),
    releaseHistory: id => this.client.call('databaseImport.releaseHistory', id),
    start: input =>
      this.client.call(
        'databaseImport.start',
        input.input.kind === 'port'
          ? transfer(input, [input.input.port])
          : input
      ),
    status: id => this.client.call('databaseImport.status', id),
    result: id => this.client.call('databaseImport.result', id),
    cancel: id => this.client.call('databaseImport.cancel', id),
    release: id => this.client.call('databaseImport.release', id),
  };

  databaseWriterMetrics() {
    return this.client.call('databaseWriter.metrics');
  }

  async mutateDatabaseRows(
    input: DatabaseRowTransaction,
    abort?: AbortSignal
  ): Promise<DatabaseRowTransactionResult> {
    abort?.throwIfAborted();
    return this.client.call('databaseWriter.rows', input, abort);
  }

  /** Row changes of several databases as one publication: one receipt. */
  async mutateDatabaseRowGroup(
    input: DatabaseRowGroupTransaction,
    abort?: AbortSignal
  ): Promise<DatabaseRowGroupTransactionResult> {
    abort?.throwIfAborted();
    return this.client.call('databaseWriter.rowGroup', input, abort);
  }

  releaseDatabaseWriterSessions(databaseRootDocId: string): Promise<void> {
    return this.client.call('databaseWriter.release', databaseRootDocId);
  }

  async mutateDatabase(
    input: DatabaseWriterMutation,
    abort?: AbortSignal
  ): Promise<DatabaseWriterMutationResult> {
    abort?.throwIfAborted();
    return this.client.call('databaseWriter.mutate', input, abort);
  }

  enableBatterySaveMode(): Promise<void> {
    return this.client.call('sync.enableBatterySaveMode');
  }
  disableBatterySaveMode(): Promise<void> {
    return this.client.call('sync.disableBatterySaveMode');
  }

  pauseSync() {
    return this.client.call('sync.pauseSync');
  }
  resumeSync() {
    return this.client.call('sync.resumeSync');
  }

  // sl-canvas2-8.24.21: Expose doc storage methods for integrity checking
  /**
   * Get a document by ID (for integrity checking).
   * Returns the full document record including binary data.
   */
  getDoc(docId: string): Promise<DocRecord | null> {
    return this.docStorage.getDoc(docId);
  }

  /**
   * Get all document timestamps (for integrity checking).
   * Returns a map of docId to last modified timestamp.
   */
  getDocTimestamps(after?: Date): Promise<Record<string, Date>> {
    return this.docStorage.getDocTimestamps(after);
  }

  /**
   * Wait until local document storage is ready for direct reads.
   */
  waitForLocalDocStorageConnected(): Promise<void> {
    return this.docStorage.connection.waitForConnected();
  }

  /**
   * Clear only local doc storage. Does not call remote delete.
   */
  resetLocalDoc(docId: string): Promise<void> {
    return this.docStorage.resetLocalDoc(docId);
  }
}

class WorkerDocStorage
  implements DocStorage, DocPublicationStorage, RefusedPublicationStorage
{
  constructor(private readonly client: OpClient<WorkerOps>) {}
  spaceId = '';

  readonly storageType = 'doc';
  readonly isReadonly = false;

  async readPublicationBase(docIds: readonly string[], signal?: AbortSignal) {
    throwIfAborted(signal);
    return this.client.call('docStorage.readPublicationBase', docIds, signal);
  }

  async commitPublication(input: LocalPublication, signal?: AbortSignal) {
    throwIfAborted(signal);
    return this.client.call('docStorage.commitPublication', input, signal);
  }

  listRefusedPublications(docIds: readonly string[]) {
    return this.client.call('docStorage.listRefusedPublications', docIds);
  }

  resolveRefusedPublications(
    docIds: readonly string[],
    resolution: RefusedPublicationResolution
  ) {
    return this.client.call('docStorage.resolveRefusedPublications', {
      docIds,
      resolution,
    });
  }

  listPendingPublications(after: number, limit: number) {
    return this.client.call('docStorage.listPendingPublications', {
      after,
      limit,
    });
  }

  beginPublicationAttempt(input: BeginPublicationAttemptInput) {
    return this.client.call('docStorage.beginPublicationAttempt', input);
  }

  acknowledgePublication(input: AcknowledgePublicationInput) {
    return this.client.call('docStorage.acknowledgePublication', input);
  }

  async applyRemotePublication(
    input: ApplyRemotePublicationInput,
    signal?: AbortSignal
  ) {
    throwIfAborted(signal);
    return this.client.call('docStorage.applyRemotePublication', input, signal);
  }

  async getDoc(docId: string) {
    return this.client.call('docStorage.getDoc', docId);
  }

  async getDocDiff(docId: string, state?: Uint8Array) {
    return this.client.call('docStorage.getDocDiff', { docId, state });
  }

  async pushDocUpdate(update: DocUpdate, origin?: string) {
    return this.client.call('docStorage.pushDocUpdate', { update, origin });
  }

  async getDocTimestamp(docId: string) {
    return this.client.call('docStorage.getDocTimestamp', docId);
  }

  async getDocStoredBytes(docId: string) {
    return this.client.call('docStorage.getDocStoredBytes', docId);
  }

  async getDocTimestamps(after?: Date) {
    return this.client.call('docStorage.getDocTimestamps', after ?? null);
  }

  async deleteDoc(docId: string) {
    return this.client.call('docStorage.deleteDoc', docId);
  }

  /**
   * Clear only local IDB state for a doc (not remote).
   * Used after server-side compaction so the sync engine
   * re-pulls the fresh snapshot from the server.
   */
  async resetLocalDoc(docId: string) {
    return this.client.call('docStorage.resetLocalDoc', docId);
  }

  /**
   * Surgically repair a corrupt doc by removing only corrupt IDB entries.
   * Validates snapshot and each individual update, deletes only corrupt ones.
   * Preserves all valid unsynced data — safe for workspace root and all docs.
   */
  async repairDoc(docId: string) {
    return this.client.call('docStorage.repairDoc', docId);
  }

  subscribeDocUpdate(callback: (update: DocRecord, origin?: string) => void) {
    const subscription = this.client
      .ob$('docStorage.subscribeDocUpdate')
      .subscribe(value => {
        callback(value.update, value.origin);
      });
    return () => {
      subscription.unsubscribe();
    };
  }

  subscribeDocCompacted(
    _callback: (info: {
      docId: string;
      originalSizeBytes: number;
      newSizeBytes: number;
      reductionPercent: number;
    }) => void
  ): () => void {
    // Compacted events come through docSync.compacted$, not storage layer
    return () => {};
  }

  subscribePermissionsChanged(
    _callback: (info: {
      workspaceId: string;
      changeType: string;
      timestamp: number;
    }) => void
  ): () => void {
    // Permission events come through docSync.permissionsChanged$, not storage layer
    return () => {};
  }

  subscribePermissionGraphAdvanced(): () => void {
    // Counter signals come through docSync.permissionGraphAdvanced$
    return () => {};
  }

  subscribeDemoContentRemoved(
    _callback: (info: DemoContentRemovedEvent) => void
  ): () => void {
    return () => {};
  }

  subscribeSyncProtocolUnsupported(
    _callback: (info: SyncProtocolUnsupportedEvent) => void
  ): () => void {
    // Protocol refusals come through docSync.syncProtocolUnsupported$
    return () => {};
  }

  subscribeSystemDocAccessReady(
    _callback: (info: SystemDocAccessReadyEvent) => void
  ): () => void {
    // Consumed by DocSyncPeer inside the worker; not exposed across it
    return () => {};
  }

  subscribeDocPersisted(
    _callback: (info: DocPersistedEvent) => void
  ): () => void {
    // Consumed by DocSyncPeer inside the worker; not exposed across it
    return () => {};
  }

  subscribeWorkspaceMetaChanged(
    _callback: (info: {
      workspaceId: string;
      changeType: string;
      docId: string;
      localMutationId?: string;
      timestamp: number;
      source: string;
    }) => void
  ): () => void {
    // Workspace meta events come through docSync.workspaceMetaChanged$, not storage layer
    return () => {};
  }

  subscribeRecoveryCompleted(
    _callback: (info: {
      workspaceId: string;
      newRecoveryVersion: number;
      timestamp: number;
    }) => void
  ): () => void {
    // Recovery events come through docSync.recoveryCompleted$, not storage layer
    return () => {};
  }

  subscribeDocSizeWarning(
    _callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      warnBytes: number;
      limitBytes: number;
    }) => void
  ): () => void {
    // Doc size events come through docSync.docSizeWarning$, not storage layer
    return () => {};
  }

  subscribeDocSizeHardLimit(
    _callback: (info: {
      docId: string;
      spaceId: string;
      sizeBytes: number;
      limitBytes: number;
    }) => void
  ): () => void {
    // Doc size events come through docSync.docSizeHardLimit$, not storage layer
    return () => {};
  }

  subscribeEmbeddingDocStatus(
    _callback: (info: {
      workspaceId: string;
      docId: string;
      status: 'indexing' | 'indexed' | 'failed';
      chunksIndexed?: number;
      chunksTotal?: number;
      timestamp: number;
    }) => void
  ): () => void {
    // Embedding events come through docSync.embeddingDocStatus$, not storage layer
    return () => {};
  }

  subscribeEmbeddingWorkspaceProgress(
    _callback: (info: {
      workspaceId: string;
      docsIndexed: number;
      docsTotal: number;
      timestamp: number;
    }) => void
  ): () => void {
    // Embedding events come through docSync.embeddingWorkspaceProgress$, not storage layer
    return () => {};
  }

  subscribeKgUpdated(
    _callback: (info: {
      workspaceId: string;
      entityCount: number;
      relationCount: number;
      timestamp: number;
    }) => void
  ): () => void {
    // KG events come through docSync.kgUpdated$, not storage layer
    return () => {};
  }

  subscribeSearchAdvanced(
    _callback: (info: WorkspaceSearchAdvancedPayload) => void
  ): () => void {
    // Search events come through docSync.searchAdvanced$, not storage layer
    return () => {};
  }

  connection = new WorkerDocConnection(this.client);
}

class WorkerDocConnection extends DummyConnection {
  constructor(private readonly client: OpClient<WorkerOps>) {
    super();
  }

  promise: Promise<void> | undefined;

  override waitForConnected(): Promise<void> {
    if (this.promise) {
      return this.promise;
    }
    this.promise = this.client
      .call('docStorage.waitForConnected')
      .catch(err => {
        this.promise = undefined;
        throw err;
      });
    return this.promise;
  }
}

class WorkerBlobStorage implements BlobStorage {
  constructor(private readonly client: OpClient<WorkerOps>) {}

  readonly storageType = 'blob';
  readonly isReadonly = false;

  get(key: string, _signal?: AbortSignal): Promise<BlobRecord | null> {
    return this.client.call('blobStorage.getBlob', key);
  }
  set(blob: BlobRecord, _signal?: AbortSignal): Promise<void> {
    return this.client.call('blobStorage.setBlob', blob);
  }

  delete(
    key: string,
    permanently: boolean,
    _signal?: AbortSignal
  ): Promise<void> {
    return this.client.call('blobStorage.deleteBlob', { key, permanently });
  }

  release(_signal?: AbortSignal): Promise<void> {
    return this.client.call('blobStorage.releaseBlobs');
  }

  list(_signal?: AbortSignal): Promise<ListedBlobRecord[]> {
    return this.client.call('blobStorage.listBlobs');
  }

  connection = new WorkerBlobConnection(this.client);
}

class WorkerBlobConnection extends DummyConnection {
  constructor(private readonly client: OpClient<WorkerOps>) {
    super();
  }

  promise: Promise<void> | undefined;

  override waitForConnected(): Promise<void> {
    if (this.promise) {
      return this.promise;
    }
    this.promise = this.client
      .call('blobStorage.waitForConnected')
      .catch(err => {
        this.promise = undefined;
        throw err;
      });
    return this.promise;
  }
}

class WorkerDocSync implements DocSync {
  private readonly importSeamReaders = new Map<
    string,
    (
      offset: number,
      signal: AbortSignal
    ) => Promise<import('../utils/import-seam-diagnostics').ImportSeamReadout>
  >();
  private importSeamReader(docId: string) {
    let reader = this.importSeamReaders.get(docId);
    if (!reader) {
      if (this.importSeamReaders.size === 32)
        this.importSeamReaders.delete(
          this.importSeamReaders.keys().next().value!
        );
      reader = (offset: number, signal: AbortSignal) =>
        this.client.call('docSync.readImportSeams', { offset, docId }, signal);
      this.importSeamReaders.set(docId, reader);
    }
    return reader;
  }
  constructor(
    private readonly client: OpClient<WorkerOps>,
    private readonly ready: Promise<unknown>
  ) {}

  get state$() {
    return this.client.ob$('docSync.state');
  }

  get corruption$() {
    return this.client.ob$('docSync.corruption');
  }

  // sl-affine-uhr: Expose compacted$ from worker
  get compacted$() {
    return this.client.ob$('docSync.compacted');
  }

  // sl-j143.5: Expose permission change events from worker
  get permissionsChanged$() {
    return this.client.ob$('docSync.permissionsChanged');
  }

  get permissionGraphAdvanced$() {
    return this.client.ob$('docSync.permissionGraphAdvanced');
  }

  // Expose workspace meta change events from worker
  get workspaceMetaChanged$() {
    return this.client.ob$('docSync.workspaceMetaChanged');
  }

  // sl-lkdb.3.2: Expose recovery completed events from worker
  get recoveryCompleted$() {
    return this.client.ob$('docSync.recoveryCompleted');
  }

  get demoContentRemoved$() {
    return this.client.ob$('docSync.demoContentRemoved');
  }

  get pageFormatUpgradeRefused$() {
    return this.client.ob$('docSync.pageFormatUpgradeRefused');
  }

  get syncProtocolUnsupported$() {
    return this.client.ob$('docSync.syncProtocolUnsupported');
  }

  // sl-lv2w.5: Expose doc size warning events from worker
  get docSizeWarning$() {
    return this.client.ob$('docSync.docSizeWarning');
  }

  // sl-lv2w.5: Expose doc size hard limit events from worker
  get docSizeHardLimit$() {
    return this.client.ob$('docSync.docSizeHardLimit');
  }

  // sl-00ro: Expose embedding doc status events from worker
  get embeddingDocStatus$() {
    return this.client.ob$('docSync.embeddingDocStatus');
  }

  // sl-00ro: Expose embedding workspace progress events from worker
  get embeddingWorkspaceProgress$() {
    return this.client.ob$('docSync.embeddingWorkspaceProgress');
  }

  get kgUpdated$() {
    return this.client.ob$('docSync.kgUpdated');
  }

  get searchAdvanced$() {
    return this.client.ob$('docSync.searchAdvanced');
  }

  docState$(docId: string) {
    return this.client.ob$('docSync.docState', docId);
  }

  async waitForSynced(docId?: string, abort?: AbortSignal): Promise<void> {
    await this.client.call('docSync.waitForSynced', docId ?? null, abort);
  }

  /**
   * sl-sync-flush: Flush all pending updates and wait for backend ACK.
   */
  async flushAndWaitForSynced(
    docId: string,
    abort?: AbortSignal
  ): Promise<void> {
    await traceImportAwait(
      'client.flush-rpc',
      docId,
      () => this.client.call('docSync.flushAndWaitForSynced', docId, abort),
      this.importSeamReader(docId)
    );
  }

  // sl-suoi.1.2: Expose hot sync complete from worker
  get hotSyncComplete$() {
    return this.client.ob$('docSync.hotSyncComplete');
  }

  get warmCacheHeld$() {
    return this.client.ob$('docSync.warmCacheHeld');
  }

  get warmCacheContents$() {
    return this.client.ob$('docSync.warmCacheContents');
  }

  // sl-suoi.1.3: Dynamically add doc to sync queue
  addDoc(docId: string, source: import('../sync/doc').DocSyncRequestSource) {
    void this.ready
      .then(() => this.client.call('docSync.addDoc', { docId, source }))
      .catch(error => {
        console.error('Failed to enqueue document for sync', docId, error);
      });
  }

  setWarmCache(selection: import('../sync/doc').WarmCacheSelection) {
    void this.ready
      .then(() => this.client.call('docSync.setWarmCache', selection))
      .catch(error => {
        console.error('Failed to set the warm cache', error);
      });
  }

  shrinkWarmCache() {
    void this.ready
      .then(() => this.client.call('docSync.shrinkWarmCache'))
      .catch(error => {
        console.error('Failed to shrink the warm cache', error);
      });
  }

  async releaseDoc(
    docId: string,
    source: import('../sync/doc').DocSyncRequestSource
  ) {
    await this.ready;
    await this.client.call('docSync.releaseDoc', { docId, source });
  }

  debugDocState(docId: string) {
    return this.client.call('docSync.debugDocState', docId);
  }

  async removeDoc(docId: string): Promise<void> {
    await this.client.call('docSync.removeDoc', docId);
  }

  addPriority(docId: string, priority: number) {
    let disposed = false;
    let subscription: { unsubscribe(): void } | undefined;

    void this.ready
      .then(() => {
        if (disposed) {
          return;
        }
        subscription = this.client
          .ob$('docSync.addPriority', { docId, priority })
          .subscribe();
      })
      .catch(error => {
        console.error('Failed to prioritize document sync', docId, error);
      });

    return () => {
      disposed = true;
      subscription?.unsubscribe();
    };
  }

  resetSync(): Promise<void> {
    return this.client.call('docSync.resetSync');
  }

  async forceReconcile(_docId: string): Promise<never[]> {
    // Bidirectional diagnostic reconciliation is not exposed by the worker.
    return [];
  }

  async pullRemote(docId: string, signal?: AbortSignal) {
    await lastValueFrom(from(this.ready).pipe(takeUntilAbort(signal)));
    // Observing readiness is cancellable; shared initialization keeps running.
    // Check again after the await before OpClient registers future abort events.
    throwIfAborted(signal);
    return this.client.call('docSync.pullRemote', docId, signal);
  }

  async repairDoc(docId: string) {
    await this.ready;
    return this.client.call('docSync.repairDoc', docId);
  }
}

class WorkerBlobSync implements BlobSync {
  constructor(private readonly client: OpClient<WorkerOps>) {}
  get state$() {
    return this.client.ob$('blobSync.state');
  }
  blobState$(blobId: string) {
    return this.client.ob$('blobSync.blobState', blobId);
  }

  downloadBlob(blobId: string): Promise<boolean> {
    return this.client.call('blobSync.downloadBlob', blobId);
  }
  uploadBlob(blob: BlobRecord, force?: boolean): Promise<true> {
    return this.client.call('blobSync.uploadBlob', { blob, force });
  }
  fullDownload(peerId?: string, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      const abortListener = () => {
        reject(signal?.reason);
        subscription.unsubscribe();
      };

      signal?.addEventListener('abort', abortListener);

      const subscription = this.client
        .ob$('blobSync.fullDownload', peerId ?? null)
        .subscribe({
          next() {
            signal?.removeEventListener('abort', abortListener);
            resolve();
          },
          error(err) {
            signal?.removeEventListener('abort', abortListener);
            reject(err);
          },
        });
    });
  }
}

class WorkerAwarenessSync implements AwarenessSync {
  constructor(private readonly client: OpClient<WorkerOps>) {}

  update(record: AwarenessRecord, origin?: string): Promise<void> {
    return this.client.call('awarenessSync.update', {
      awareness: record,
      origin,
    });
  }

  subscribeUpdate(
    id: string,
    onUpdate: (update: AwarenessRecord, origin?: string) => void,
    onCollect: () => Promise<AwarenessRecord | null>
  ): () => void {
    const subscription = this.client
      .ob$('awarenessSync.subscribeUpdate', id)
      .subscribe({
        next: update => {
          if (update.type === 'awareness-update') {
            onUpdate(update.awareness, update.origin);
          }
          if (update.type === 'awareness-collect') {
            onCollect()
              .then(record => {
                if (record) {
                  this.client
                    .call('awarenessSync.collect', {
                      awareness: record,
                      collectId: update.collectId,
                    })
                    .catch(err => {
                      console.error('error feedback collected awareness', err);
                    });
                }
              })
              .catch(err => {
                console.error('error collecting awareness', err);
              });
          }
        },
      });
    return () => {
      subscription.unsubscribe();
    };
  }
}

class WorkerIndexerSync implements IndexerSync {
  constructor(private readonly client: OpClient<WorkerOps>) {}

  search<T extends keyof IndexerSchema, const O extends SearchOptions<T>>(
    table: T,
    query: Query<T>,
    options?: O & { prefer?: IndexerPreferOptions }
  ): Promise<SearchResult<T, O>> {
    return this.client.call('indexerSync.search', { table, query, options });
  }
  aggregate<T extends keyof IndexerSchema, const O extends AggregateOptions<T>>(
    table: T,
    query: Query<T>,
    field: keyof IndexerSchema[T],
    options?: O & { prefer?: IndexerPreferOptions }
  ): Promise<AggregateResult<T, O>> {
    return this.client.call('indexerSync.aggregate', {
      table,
      query,
      field: field as string,
      options,
    });
  }
  search$<T extends keyof IndexerSchema, const O extends SearchOptions<T>>(
    table: T,
    query: Query<T>,
    options?: O & { prefer?: IndexerPreferOptions }
  ): Observable<SearchResult<T, O>> {
    return this.client.ob$('indexerSync.subscribeSearch', {
      table,
      query,
      options,
    });
  }
  aggregate$<
    T extends keyof IndexerSchema,
    const O extends AggregateOptions<T>,
  >(
    table: T,
    query: Query<T>,
    field: keyof IndexerSchema[T],
    options?: O & { prefer?: IndexerPreferOptions }
  ): Observable<AggregateResult<T, O>> {
    return this.client.ob$('indexerSync.subscribeAggregate', {
      table,
      query,
      field: field as string,
      options,
    });
  }

  waitForCompleted(signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      const abortListener = () => {
        reject(signal?.reason);
        subscription.unsubscribe();
      };

      signal?.addEventListener('abort', abortListener);

      const subscription = this.client
        .ob$('indexerSync.waitForCompleted')
        .subscribe({
          complete() {
            signal?.removeEventListener('abort', abortListener);
            resolve();
          },
          error(err) {
            signal?.removeEventListener('abort', abortListener);
            reject(err);
          },
        });
    });
  }
  waitForDocCompleted(docId: string, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      const abortListener = () => {
        reject(signal?.reason);
        subscription.unsubscribe();
      };

      signal?.addEventListener('abort', abortListener);

      const subscription = this.client
        .ob$('indexerSync.waitForDocCompleted', docId)
        .subscribe({
          complete() {
            signal?.removeEventListener('abort', abortListener);
            resolve();
          },
          error(err) {
            signal?.removeEventListener('abort', abortListener);
            reject(err);
          },
        });
    });
  }
  get state$() {
    return this.client.ob$('indexerSync.state');
  }
  docState$(docId: string) {
    return this.client.ob$('indexerSync.docState', docId);
  }
  getDatabaseSubdocIds(ownerDocId: string): Promise<string[]> {
    return this.client.call('indexerSync.getDatabaseSubdocIds', ownerDocId);
  }
  getDatabaseSearchCoverage(
    databaseRootDocId: string
  ): Promise<DatabaseSearchCoverage> {
    return this.client.call(
      'indexerSync.getDatabaseSearchCoverage',
      databaseRootDocId
    );
  }
  getDatabaseSearchGaps(): Promise<DatabaseSearchGap[]> {
    return this.client.call('indexerSync.getDatabaseSearchGaps');
  }
  getLocalSearchStats(): Promise<LocalSearchStats> {
    return this.client.call('indexerSync.getLocalSearchStats');
  }
  writeServerSnapshot(pages: readonly SearchSnapshotPage[]) {
    this.client
      .call('indexerSync.writeServerSnapshot', [...pages])
      .catch(error => {
        console.error('failed to offer the server search content', error);
      });
  }
  writeServerMails(changes: readonly SearchSnapshotMails[]) {
    this.client
      .call('indexerSync.writeServerMails', [...changes])
      .catch(error => {
        console.error('failed to offer the server mails', error);
      });
  }
  writeServerFileNames(changes: readonly SearchSnapshotFileNames[]) {
    this.client
      .call('indexerSync.writeServerFileNames', [...changes])
      .catch(error => {
        console.error('failed to offer the server file names', error);
      });
  }
  removeFromIndex(docIds: readonly string[]) {
    this.client
      .call('indexerSync.removeFromIndex', [...docIds])
      .catch(error => {
        console.error('failed to remove revoked pages from search', error);
      });
  }
  addPriority(docId: string, priority: number) {
    const subscription = this.client
      .ob$('indexerSync.addPriority', { docId, priority })
      .subscribe();
    return () => {
      subscription.unsubscribe();
    };
  }
}
